import { clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { Card, LoungeTypes } from '@/constants';
import {
  supportedMarkets,
  supportedLocales,
} from '@/constants/supportedLocales';
import { mycaCardTypes, lineOfBusinessTypes } from '@/constants/productMapping';
import { WAITLIST_SCROLL_RESTORE_TTL_MS } from '@/constants/waitlistLounges';
import { isGuestBenefitEligibleAccount } from './accountHelper';

// Setup Tailwind-Merge to account for the tailwind-prefix, "tw-", that we use in this project.
// Otherwise tailwind-merge will fail to merge class names that have the "tw-" prefix.
// For example, passing "tw-px-4 tw-px-6" to tailwind-merge should output "tw-px-6", but
// without the prefix configuration it would output the unchanged classlist: "tw-px-4 tw-px-6"
// We initialize the twMerge function with the prefix configuration once here rather than within
// the CN utility because otherwise it would be reinitialized on every call to CN, which was
// causing unit test failures in some cases.
const twMerge = extendTailwindMerge({ prefix: 'tw-' });

export const GLOBAL_HEADER_ID = 'lounge-finder-global-header';
export const DEFAULT_STICKY_GLOBAL_HEADER_OFFSET = 149;

/**
 * Returns the space occupied by the sticky Amex and Lounge Finder headers,
 * plus a small gap so anchored content is not flush against the header.
 */
export function getStickyGlobalHeaderOffset() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return DEFAULT_STICKY_GLOBAL_HEADER_OFFSET;
  }

  const globalHeader = document.querySelector(`#${GLOBAL_HEADER_ID}`);
  if (!globalHeader) return DEFAULT_STICKY_GLOBAL_HEADER_OFFSET;

  const { height } = globalHeader.getBoundingClientRect();
  const stickyTop = Number.parseFloat(
    window.getComputedStyle(globalHeader).top
  );

  if (!height || !Number.isFinite(stickyTop)) {
    return DEFAULT_STICKY_GLOBAL_HEADER_OFFSET;
  }

  const anchorGap = 16;
  return Math.ceil(stickyTop + height + anchorGap);
}

/**
 * Generates a safe anchor ID for navigation and content linking.
 * @param {Object} params
 * @param {'chef'|'partner'|'faq'} params.type
 * @param {string} [params.name]
 * @param {string} [params.title]
 * @param {number} [params.idx]
 * @param {string} [params.section]
 * @returns {string}
 */
// Polyfill for CSS.escape for SSR/Node environments
function cssEscape(str) {
  // Always perform a consistent normalization (server & client) so anchor IDs
  // are stable regardless of environment or the presence of window.CSS.escape.
  const s = String(str || '');
  return s
    .normalize('NFKD')
    .replace(/[^\dA-Za-z-]/g, '-') // replace non-alnum/dash with dash
    .replace(/-+/g, '-') // collapse multiple dashes
    .replace(/^-+|-+$/g, ''); // trim leading/trailing dashes
}

export function getAnchorId({ type, name, title, idx, section }) {
  const escapedString = (s) =>
    s ? cssEscape(s.replace(/\s+/g, '-').toLowerCase()) : idx;
  if (type) {
    return `${type}-${escapedString(name || title)}`;
  }
  return cssEscape(`${section || ''}-${idx}`);
}

export function normalizeProviderKey(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^\da-z]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Resolve a media value into a usable URL string.
 * Supports Strapi media objects (v3/v4 shapes) and a simple URL string.
 * Returns an empty string when no usable URL is found.
 */
export function resolveMediaUrl(media) {
  if (!media) return '';
  if (typeof media === 'string') return media;
  if (media.url) return media.url;
  const attrs = (media.data && media.data.attributes) || media.attributes;
  if (attrs && attrs.url) return attrs.url;
  const formats = (attrs && attrs.formats) || media.formats;
  if (formats) {
    const prefer = ['large', 'medium', 'small', 'thumbnail'];
    const found = prefer.find((p) => formats[p] && formats[p].url);
    if (found) return formats[found].url;
    const vals = Object.values(formats);
    if (vals && vals[0] && vals[0].url) return vals[0].url;
  }
  return '';
}

/**
 * Resolve a media value against an optional asset base URL.
 * Absolute HTTP(S) URLs are preserved, while root-relative and relative paths
 * are resolved with standard URL semantics.
 */
export function resolveImageUrl(media, baseUrl) {
  const value = resolveMediaUrl(media);
  if (!value) return '';

  try {
    const resolved = baseUrl ? new URL(value, baseUrl) : new URL(value);
    return ['http:', 'https:'].includes(resolved.protocol)
      ? resolved.toString()
      : '';
  } catch {
    return '';
  }
}

export function getLang(locale) {
  if (locale === 'fr-CA') {
    return 'fr-FR';
  }
  return locale;
}

export const getSafeLocaleUtil = (unsafeLocale) => {
  try {
    const matchingLocale = supportedLocales.find(
      (locale) => locale.toLowerCase() === unsafeLocale.toLowerCase()
    );
    if (matchingLocale) {
      return matchingLocale;
    }
    // eslint-disable-next-line no-empty -- just using try to cover multiple non-breaking cases
  } catch (e) {}
  return null;
};

export const isInvalidLoungeFinderRoute = ({
  locale,
  product,
  enabledMarketProducts,
}) => {
  if (!locale || !product) {
    return true;
  }

  const localeAllLower = locale.toLowerCase();
  const supportedMarket = supportedMarkets.find(
    (market) => market.locale === localeAllLower
  );

  // invalid route if Market not in supported markets array
  if (!supportedMarket) {
    return true;
  }

  const hasProduct = supportedMarket.products.some(
    (supportedProduct) => supportedProduct.name === product
  );

  // invalid route if product not in supported products for the locale's market
  if (!hasProduct) {
    return true;
  }

  if (enabledMarketProducts && enabledMarketProducts.size > 0) {
    return !enabledMarketProducts.has(`${localeAllLower}:${product}`);
  }

  return false;
};

export function getCountryCode(product, locale) {
  if (product?.includes('-idc')) {
    /*
     "country", in the case of this function, is used to tell the user that the content they're viewing -- for the 
     card they have selected -- is related to the international dollar card rather than one specic country.
    */
    return 'International Dollar Card';
  }
  const lowerCaseLocale = locale ? locale.toLowerCase() : 'en-us';
  const supportedMarket = supportedMarkets.find(
    (market) => market.locale === lowerCaseLocale
  );

  // return country code if available, if not full country name
  return supportedMarket?.code || supportedMarket?.name || 'US';
}

export function getQueryParams(name) {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);
  return url.searchParams.get(name);
}

export function getCardArtUrl({
  baseOneCmsImgUrl,
  product,
  locale,
  isMedium = false,
}) {
  // handle default case with no locale query param
  const medium = isMedium ? '-medium' : '';
  const imageLocale = locale || 'en-US';
  return `${baseOneCmsImgUrl}card-art/${product}_${imageLocale.toLocaleLowerCase()}${medium}.png`;
}

export function resizeSelectElementToCurrentValue(select) {
  const newSelect = select;
  // remove each <option> except the current value
  const selectValues = [];
  newSelect.querySelectorAll('option').forEach((option) => {
    selectValues.push(option);
    if (option.getAttribute('value') !== newSelect.value) {
      option.remove();
    }
  });

  // allow the <select> to auto-size
  newSelect.style.width = null;

  // copy that width to "freeze" it
  newSelect.style.width = `${newSelect.clientWidth + 20}px`;

  // restore the <select>'s children
  newSelect.textContent = '';
  selectValues.forEach((option) => newSelect.append(option));
  return newSelect;
}

export function getBasePathFromLocation(locale) {
  return `/${locale.toLocaleLowerCase()}/travel/lounges`;
}

export function getMarketName(locale) {
  const [lang = 'en', region = 'US'] = locale.split('-');
  const countryName = Intl.DisplayNames
    ? new Intl.DisplayNames([lang], {
        type: 'region',
        style: 'short',
      }).of(region.toUpperCase())
    : region;

  return countryName;
}

/**
 * Formats text by replacing variables in the text with their corresponding values.
 *
 * @param {string} text Text to format.
 * @param {object} variables Object containing variables to replace in the text.
 * @returns {string} Text with variables replaced.
 */
export function formatText(text, variables) {
  let formattedText = text;
  Object.keys(variables).forEach((key) => {
    formattedText = formattedText.replace(
      new RegExp(`{${key}}`, 'g'),
      variables[key]
    );
  });
  return formattedText;
}

export const setAXPLocaleCookie = (locale) => {
  if (!locale || typeof document === 'undefined') return;
  try {
    document.cookie = `axplocale=${locale}; path=/ ;domain=.americanexpress.com`;
  } catch (error) {
    // this can fail if the cookie is blocked
    // eslint-disable-next-line no-console -- helpful error in the terminal or console
    console.error(`setAXPLocaleCookie error: ${error?.message}`);
  }
};

/**
 * Returns the Lounge Finder product based on product type in the user/profile api response.
 *
 * @param {Array} types MYCA product types returned via the user profile api.
 * @param {string} lang Language code associated with the account from MYCA (EN, ES, etc)
 * @returns {string} Lounge Finder product name.
 */
export const getProduct = (types) => {
  if (!Array.isArray(types)) {
    return Card.Platinum;
  }

  if (types.includes(mycaCardTypes.platinum)) {
    return Card.Platinum;
  }
  if (types.includes(mycaCardTypes.centurion)) {
    return Card.Centurion;
  }
  if (types.includes(mycaCardTypes.deltaReserve)) {
    return Card.Delta;
  }

  return Card.Platinum;
};
/**
 * Filters and sorts cards based on eligibility, type priority, and line of business priority.
 *
 * @param {Array} wallet Array of card objects.
 * @returns {Array} Sorted array of eligible and prioritized cards.
 */
export const getEligibleAndPrioritizedCards = (wallet) => {
  const cardTypesByPriority = [
    'the-centurion-card',
    'the-platinum-card',
    'delta-reserve',
  ];
  const lineOfBusinessPriority = [
    lineOfBusinessTypes.Consumer,
    lineOfBusinessTypes.Business,
    lineOfBusinessTypes.Corporate,
  ];

  return wallet
    .filter((card) => card.hasLoungeBenefit && card.active)
    .sort((a, b) => {
      // Sort by card type priority
      const typePriorityA = cardTypesByPriority.indexOf(a?.loungeFinderProduct);
      const typePriorityB = cardTypesByPriority.indexOf(b?.loungeFinderProduct);

      if (typePriorityA !== typePriorityB) {
        return typePriorityA - typePriorityB;
      }

      // If types are the same, sort by line of business priority
      const lobPriorityA = lineOfBusinessPriority.indexOf(
        a.lineOfBusinessTypeName
      );
      const lobPriorityB = lineOfBusinessPriority.indexOf(
        b.lineOfBusinessTypeName
      );

      return lobPriorityA - lobPriorityB;
    });
};

export const sortAndIdentifyCardProducts = (accounts) => {
  const identifiedAccountProducts = accounts.map((acct) => {
    const loungeFinderProduct = getProduct(acct?.product?.type);
    return {
      ...acct,
      loungeFinderProduct,
    };
  });

  const identifiedGuestBenefitEligibleAccounts = identifiedAccountProducts.map(
    (acct) => {
      const guestBenefitEligible = isGuestBenefitEligibleAccount(acct);
      return {
        ...acct,
        guestBenefitEligible,
      };
    }
  );

  const sortedAccounts = getEligibleAndPrioritizedCards(
    identifiedGuestBenefitEligibleAccounts
  );

  return sortedAccounts;
};

/**
 * Splits the Access & Guest policy header to be used in the Lounge Finder.
 *
 * @param {string} header agTab property from the OneCMS data (Access & Guests).
 * @returns {Array} Array with values split (ex: [Access, Guests]).
 */
export const splitAccessAndGuestPolicyHeader = (header) => {
  // Across all locales, aside from the exception locales: zh-cn, zh-hk, zh-tw, and ja-jp the "ag_tab" property from the OneCMS data contains (in various languages) the word "Access", the word "Guest", and either the word "and" or an ampersand (surrounded by space characters) between those words. In those exception locales, there's no space before/after the "and" word/symbol. This Regex covers all those current scenarios
  // eslint-disable-next-line unicorn/better-regex  -- the RegEx ES Lint Rule wants to swap "and" with "adn" then remove the vertical pipe that separates the different strings we want to remove. Functionally it works the same, but it makes it difficult for other devs to understand that the goal is to split where " and " or " & " is found in the string.
  return (header || '').split(/\s[&|and|e|y|et|und]+\s|及|與/);
};

/**
 * Returns a boolean value indicating whether the lounge's provider is Priority Pass.
 *
 * @param {array} accessRules Lounge Access Rules from the Lounges API.
 * @returns {Boolean} true if the lounge's provider is Priority Pass, false otherwise.
 */
export const isPriorityPassLounge = (accessRules) => {
  return accessRules?.[0]?.provider_id === 'PROVIDER_PRIORITY_PASS';
};

/**
 * Adds a trailing slash to a path if missing.
 * @param {string} path
 * @returns {string}
 */
export function addTrailingSlashIfMissing(path) {
  if (typeof path === 'string' && path.length > 0 && !path.endsWith('/')) {
    return `${path}/`;
  }
  return path;
}

/**
 * Smooth scroll an element into view with a configurable offset.
 * Supports window scrolling and scrolling within a container element.
 *
 * @param {Object} params
 * @param {Element | null} params.element target element to scroll to
 * @param {number} [params.offset=0] pixels to offset from the target top
 * @param {Window | HTMLElement} [params.scrollTarget=window] scroll context
 * @returns {boolean} true when a scroll action is triggered, otherwise false
 */
export function smoothScrollToElement({
  element,
  offset = 0,
  scrollTarget = window,
}) {
  if (!element || typeof window === 'undefined') {
    return false;
  }

  if ('scrollY' in scrollTarget) {
    const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    return true;
  }

  const targetRect = scrollTarget.getBoundingClientRect();
  const y =
    element.getBoundingClientRect().top -
    targetRect.top +
    scrollTarget.scrollTop -
    offset;
  scrollTarget.scrollTo({ top: y, behavior: 'smooth' });
  return true;
}

/**
 * Parses a CTA link value into a hash selector for same-page scrolling
 * and a full destination path for cross-page navigation.
 *
 * @param {string} link - Normalized CTA link value (trimmed, with fallback applied)
 * @param {string} defaultAnchor - Fallback anchor (e.g. '#centurion-network-lounges-header')
 * @returns {{ hashSelector: string, ctaDestination: string }}
 */
export function resolveCtaLinkTargets(link, defaultAnchor) {
  if (link.startsWith('#')) {
    return { hashSelector: link, ctaDestination: `centurion-network/${link}` };
  }
  if (link.includes('#')) {
    return { hashSelector: `#${link.split('#')[1]}`, ctaDestination: link };
  }
  return {
    hashSelector: defaultAnchor,
    ctaDestination: `${link}${defaultAnchor}`,
  };
}

/**
 * Builds a URL string with a trailing slash on the pathname, plus search and hash.
 * @param {string} pathname
 * @param {string} [search]
 * @param {string} [hash]
 * @returns {string}
 */
export function getUrlWithTrailingSlash(pathname, search = '', hash = '') {
  const pathWithSlash = addTrailingSlashIfMissing(pathname);
  const searchPart = search && search !== '?' ? `${search}` : '';
  const hashPart = hash && hash !== '#' ? `${hash}` : '';
  return `${pathWithSlash}${searchPart}${hashPart}`;
}

const getCurrentPathWithSearch = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
};

export const saveScrollPositionForRestore = ({ storageKey }) => {
  if (typeof window === 'undefined' || !storageKey) return;

  const payload = {
    path: getCurrentPathWithSearch(),
    scrollY: window.scrollY,
    timestamp: Date.now(),
  };

  window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
};

export const restoreScrollPosition = ({
  storageKey,
  ttlMs = WAITLIST_SCROLL_RESTORE_TTL_MS,
}) => {
  if (typeof window === 'undefined' || !storageKey) return;

  const savedValue = window.sessionStorage.getItem(storageKey);
  if (!savedValue) return;

  const clearSavedValue = () => {
    window.sessionStorage.removeItem(storageKey);
  };

  try {
    const { path, scrollY, timestamp } = JSON.parse(savedValue);
    const isStale = Date.now() - timestamp > ttlMs;

    if (isStale) {
      clearSavedValue();
      return;
    }

    if (path !== getCurrentPathWithSearch()) {
      return;
    }

    if (typeof scrollY === 'number') {
      window.scrollTo(0, scrollY);
      // Refresh timestamp so TTL resets on each restore (keeps entry fresh during session)
      const updatedPayload = { path, scrollY, timestamp: Date.now() };
      window.sessionStorage.setItem(storageKey, JSON.stringify(updatedPayload));
    }
  } catch {
    clearSavedValue();
  }
};

export const saveScrollBeforeExternalNavigation = ({ storageKey }) => {
  try {
    saveScrollPositionForRestore({ storageKey });
  } catch {
    // Allow navigation to continue if session storage is blocked.
  }
};

export const isCenturionProvider = (providerId) =>
  Boolean(
    providerId?.includes('CENTURION') || providerId === 'PROVIDER_AMEX_BR_GRU'
  );

export const isCenturionLounge = (lounge) =>
  Boolean(
    lounge &&
      lounge.experience_type !== LoungeTypes.SIDECAR &&
      (lounge.experience_type === LoungeTypes.CENTURION ||
        lounge.access_rules?.some(({ provider_id: providerId }) =>
          isCenturionProvider(providerId)
        ))
  );

/**
 * Filter Centurion providers in `the-platinum-card`, `the-centurion-card` etc files parners array so only one is displayed
 * on the partner tiles and home page terms & conditions modal. (each country centurion provider is a separate array entry)
 * (PROVIDER_AMEX_BR_GRU is also a Centurion Provider)
 * @param {array} partners partners array from the OneCMS card specific files
 * @returns {array}
 */
export function filterCenturionProvidersForDisplay(partners) {
  let foundCenturionProvider = false;
  const filteredPartners = partners.filter((partner) => {
    if (isCenturionProvider(partner.provider_id)) {
      if (!foundCenturionProvider) {
        foundCenturionProvider = true;
        return true;
      }
      return false;
    }
    return true;
  });

  return filteredPartners;
}
